import React from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { IntegrationAccessProvider } from '@/contexts/IntegrationAccessContext';
import {
  SignInPage,
  ShowcasePage,
  PublicTrackPage,
  PublicTrackLandingPage,
} from '@/pages';
import { Layout } from '@/components';
import { routes } from '@/router';
import { APP_CONFIG } from '@/config/app';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Spinner } from '@/components/ui/spinner';

const AuthLoadingScreen: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center bg-bg-white-0">
    <div className="flex flex-col items-center gap-4">
      <Spinner size="xl" tone="primary" />
      <p className="text-sm text-text-sub-600">Loading...</p>
    </div>
  </div>
);

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated, isInitializing } = useAuth();
  const location = useLocation();

  // Full-page loader only while restoring a session on first load —
  // never during an in-flight sign-in/out action.
  if (isInitializing) return <AuthLoadingScreen />;
  if (!isAuthenticated) return <Navigate to="/signin" replace />;

  return <Layout currentPath={location.pathname}>{children}</Layout>;
};

const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isInitializing } = useAuth();

  if (isInitializing) return <AuthLoadingScreen />;
  if (isAuthenticated) return <Navigate to={APP_CONFIG.homeRoute} replace />;

  return <>{children}</>;
};

const AppContent: React.FC = () => {
  return (
    <Routes>
      <Route
        path="/signin"
        element={
          <PublicRoute>
            <SignInPage />
          </PublicRoute>
        }
      />
      {/* Public — DS component gallery, no auth required */}
      <Route path="/showcase" element={<ShowcasePage />} />
      {/* Public — QR shipment tracking (gov assessment), no auth required.
          Both the landing (search + scan) and the detail deep-link are
          deliberately OUTSIDE ProtectedRoute. */}
      <Route path="/track" element={<PublicTrackLandingPage />} />
      <Route path="/track/:waybill" element={<PublicTrackPage />} />
      {routes
        .filter((route) => route.path !== '/showcase')
        .map((route) => (
          <Route
            key={route.path}
            path={route.path}
            element={<ProtectedRoute>{route.element}</ProtectedRoute>}
          />
        ))}
    </Routes>
  );
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <IntegrationAccessProvider>
          <div className="App">
            <AppContent />
            <ToastContainer
              position="bottom-right"
              autoClose={5000}
              hideProgressBar={false}
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
              theme="colored"
            />
          </div>
        </IntegrationAccessProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
